import ExcelJS from "exceljs";

// Mantiene el mismo criterio de formato "legible" que la vista.
function humanize(hours) {
  if (hours === null || hours === undefined || hours === "") return "—";
  const h = Number(hours);
  if (isNaN(h)) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} días`;
}

function round2(v) {
  const n = Number(v);
  return isNaN(n) ? null : Math.round(n * 100) / 100;
}

const C = {
  primary: "FF1976D2",
  dark: "FF0E1B2C",
  slate: "FF4A5568",
  muted: "FF8A98AB",
  band: "FFEEF3FB", // fondo suave para bandas de sección
  zebra: "FFF6F8FB",
  cardFill: "FFF4F8FF",
  border: "FFE2E8F0",
  white: "FFFFFFFF",
};

const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: C.primary } };
const HEADER_FONT = { bold: true, color: { argb: C.white }, size: 11 };

function thinBorder() {
  return {
    top: { style: "thin", color: { argb: C.border } },
    bottom: { style: "thin", color: { argb: C.border } },
    left: { style: "thin", color: { argb: C.border } },
    right: { style: "thin", color: { argb: C.border } },
  };
}

function styleHeaderRow(row) {
  row.height = 20;
  row.eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = thinBorder();
  });
}

// Banda de sección de ancho completo (A:F) con fondo suave.
function sectionBand(sheet, rowNum, text) {
  sheet.mergeCells(`A${rowNum}:F${rowNum}`);
  const cell = sheet.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: C.dark } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.band } };
  cell.alignment = { vertical: "middle" };
  sheet.getRow(rowNum).height = 22;
}

function addImageFromDataURI(workbook, sheet, dataURI, startRow, opts = {}) {
  if (!dataURI) return startRow;
  const base64 = dataURI.includes(",") ? dataURI.split(",")[1] : dataURI;
  const imageId = workbook.addImage({ base64, extension: "png" });
  const width = opts.width || 760;
  const height = opts.height || 320;
  sheet.addImage(imageId, {
    tl: { col: 0.2, row: startRow },
    ext: { width, height },
    editAs: "oneCell",
  });
  // Aproxima cuántas filas ocupa la imagen (≈20px por fila).
  return startRow + Math.ceil(height / 20) + 1;
}

/**
 * Genera el Excel del reporte de estadísticas de compras.
 */
export async function generateShopReportsExcel(p) {
  const {
    summary = {},
    path = null,
    pathFromName = "",
    pathToName = "",
    transitions = [],
    rawTransitions = [],
    approvers = [],
    meta = {},
    charts = {},
  } = p;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Finance Connected";
  workbook.created = new Date();

  /* ------------------------------ Resumen ------------------------------ */
  const resumen = workbook.addWorksheet("Resumen", {
    views: [{ showGridLines: false }],
  });
  resumen.columns = [
    { width: 42 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
    { width: 20 },
  ];

  // Encabezado principal.
  resumen.mergeCells("A1:F1");
  const h1 = resumen.getCell("A1");
  h1.value = "Reportes de compras — Estadísticas";
  h1.font = { bold: true, size: 16, color: { argb: C.white } };
  h1.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.dark } };
  h1.alignment = { vertical: "middle", indent: 1 };
  resumen.getRow(1).height = 32;

  resumen.mergeCells("A2:F2");
  const h2 = resumen.getCell("A2");
  h2.value = meta.businessName || "";
  h2.font = { bold: true, size: 11, color: { argb: C.slate } };
  h2.alignment = { indent: 1 };

  resumen.mergeCells("A3:F3");
  const h3 = resumen.getCell("A3");
  h3.value =
    meta.dateStart && meta.dateEnd
      ? `Período: ${meta.dateStart} — ${meta.dateEnd}`
      : "";
  h3.font = { size: 10, color: { argb: C.muted } };
  h3.alignment = { indent: 1 };

  let r = 5;
  sectionBand(resumen, r, "Indicadores generales");
  r += 2;

  const kpis = [
    ["Transiciones analizadas", summary.total_transitions ?? 0],
    ["Órdenes con movimiento", summary.orders_analyzed ?? 0],
    ["Órdenes cerradas", summary.closed_orders ?? 0],
    ["Órdenes canceladas", summary.cancelled_orders ?? 0],
    ["Órdenes rechazadas", summary.rejected_orders ?? 0],
    ["Ciclo promedio creación → cerrada", humanize(summary.avg_cycle_hours)],
    ["Mediana del ciclo", humanize(summary.median_cycle_hours)],
  ];
  kpis.forEach(([label, value], i) => {
    const row = resumen.getRow(r);
    row.height = 22;
    const lc = resumen.getCell(`A${r}`);
    lc.value = label;
    lc.font = { color: { argb: C.slate }, size: 11 };
    lc.alignment = { vertical: "middle", indent: 1 };
    resumen.mergeCells(`B${r}:C${r}`);
    const vc = resumen.getCell(`B${r}`);
    vc.value = value;
    vc.font = { bold: true, size: 12, color: { argb: C.dark } };
    vc.alignment = { vertical: "middle", horizontal: "center" };
    // Tarjeta: fondo + borde en las 3 columnas (A, B, C).
    ["A", "B", "C"].forEach((col) => {
      const c = resumen.getCell(`${col}${r}`);
      c.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: i % 2 === 0 ? C.cardFill : C.white },
      };
      c.border = thinBorder();
    });
    r++;
  });

  r += 1;
  sectionBand(resumen, r, "Tiempo entre dos estatus");
  r += 1;

  if (path && path.orders) {
    resumen.mergeCells(`A${r}:F${r}`);
    const desc = resumen.getCell(`A${r}`);
    desc.value = `De "${pathFromName}" a "${pathToName}" (${path.orders} órdenes)`;
    desc.font = { size: 10, italic: true, color: { argb: C.muted } };
    r++;

    const headerRow = resumen.getRow(r);
    headerRow.values = ["", "Promedio", "Mediana", "Mínimo", "Máximo"];
    styleHeaderRow(headerRow);
    resumen.getCell(`A${r}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.white },
    };
    resumen.getCell(`A${r}`).border = thinBorder();
    r++;

    const rows = [
      ["Horas", "avg_hours", "median_hours", "min_hours", "max_hours", round2],
      ["Legible", "avg_hours", "median_hours", "min_hours", "max_hours", humanize],
    ];
    rows.forEach(([label, ...cols], idx) => {
      const fn = cols.pop();
      const row = resumen.getRow(r);
      row.getCell(1).value = label;
      row.getCell(1).font = { bold: true, color: { argb: C.slate } };
      cols.forEach((key, j) => {
        row.getCell(j + 2).value = fn(path[key]);
        row.getCell(j + 2).alignment = { horizontal: "center" };
      });
      for (let col = 1; col <= 5; col++) {
        row.getCell(col).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: idx % 2 === 0 ? C.zebra : C.white },
        };
        row.getCell(col).border = thinBorder();
      }
      r++;
    });
  } else {
    resumen.mergeCells(`A${r}:F${r}`);
    resumen.getCell(`A${r}`).value =
      "Sin datos para el par de estatus seleccionado.";
    resumen.getCell(`A${r}`).font = { size: 10, italic: true, color: { argb: C.muted } };
    r++;
  }

  // Gráficos incrustados.
  r += 2;
  if (charts.transition) {
    sectionBand(resumen, r, "Tiempo promedio por transición de estatus");
    r += 2;
    r = addImageFromDataURI(workbook, resumen, charts.transition, r, {
      width: 760,
      height: charts.transitionHeight || 360,
    });
  }
  if (charts.approver) {
    r += 1;
    sectionBand(resumen, r, "Tiempo promedio por aprobador");
    r += 2;
    r = addImageFromDataURI(workbook, resumen, charts.approver, r, {
      width: 760,
      height: charts.approverHeight || 360,
    });
  }

  /* --------------------------- Transiciones ---------------------------- */
  const tSheet = workbook.addWorksheet("Transiciones");
  tSheet.columns = [
    { header: "Desde", key: "from_name", width: 26 },
    { header: "Hacia", key: "to_name", width: 26 },
    { header: "Transiciones", key: "transitions", width: 14 },
    { header: "Promedio (h)", key: "avg_hours", width: 14 },
    { header: "Mediana (h)", key: "median_hours", width: 14 },
    { header: "Mínimo (h)", key: "min_hours", width: 14 },
    { header: "Máximo (h)", key: "max_hours", width: 14 },
    { header: "Promedio (legible)", key: "avg_human", width: 18 },
  ];
  styleHeaderRow(tSheet.getRow(1));
  transitions.forEach((row, i) => {
    const added = tSheet.addRow({
      from_name: row.from_name,
      to_name: row.to_name,
      transitions: Number(row.transitions) || 0,
      avg_hours: round2(row.avg_hours),
      median_hours: round2(row.median_hours),
      min_hours: round2(row.min_hours),
      max_hours: round2(row.max_hours),
      avg_human: humanize(row.avg_hours),
    });
    if (i % 2 === 1) {
      added.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.zebra } };
      });
    }
  });
  tSheet.views = [{ state: "frozen", ySplit: 1 }];

  /* ---------------------------- Aprobadores ---------------------------- */
  const aSheet = workbook.addWorksheet("Aprobadores");
  aSheet.columns = [
    { header: "Aprobador", key: "approver", width: 34 },
    { header: "Aprobaciones", key: "approvals", width: 14 },
    { header: "Promedio (h)", key: "avg_hours", width: 14 },
    { header: "Mediana (h)", key: "median_hours", width: 14 },
    { header: "Mínimo (h)", key: "min_hours", width: 14 },
    { header: "Máximo (h)", key: "max_hours", width: 14 },
    { header: "Promedio (legible)", key: "avg_human", width: 18 },
  ];
  styleHeaderRow(aSheet.getRow(1));
  approvers.forEach((row, i) => {
    const added = aSheet.addRow({
      approver: row.approver,
      approvals: Number(row.approvals) || 0,
      avg_hours: round2(row.avg_hours),
      median_hours: round2(row.median_hours),
      min_hours: round2(row.min_hours),
      max_hours: round2(row.max_hours),
      avg_human: humanize(row.avg_hours),
    });
    if (i % 2 === 1) {
      added.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.zebra } };
      });
    }
  });
  aSheet.views = [{ state: "frozen", ySplit: 1 }];

  /* -------------------------- Datos en bruto --------------------------- */
  // Todas las transiciones sin filtrar (incluye retrocesos y anulaciones)
  // con las horas sin redondear, tal cual las devuelve el backend.
  const rawSource = rawTransitions.length ? rawTransitions : transitions;
  const rSheet = workbook.addWorksheet("Datos en bruto");
  rSheet.columns = [
    { header: "from_id", key: "from_id", width: 10 },
    { header: "Desde", key: "from_name", width: 26 },
    { header: "to_id", key: "to_id", width: 10 },
    { header: "Hacia", key: "to_name", width: 26 },
    { header: "Dirección", key: "direction", width: 14 },
    { header: "Transiciones", key: "transitions", width: 14 },
    { header: "avg_hours", key: "avg_hours", width: 16 },
    { header: "median_hours", key: "median_hours", width: 16 },
    { header: "min_hours", key: "min_hours", width: 16 },
    { header: "max_hours", key: "max_hours", width: 16 },
  ];
  styleHeaderRow(rSheet.getRow(1));
  rawSource.forEach((row, i) => {
    const added = rSheet.addRow({
      from_id: row.from_id,
      from_name: row.from_name,
      to_id: row.to_id,
      to_name: row.to_name,
      direction: row.direction || "",
      transitions: Number(row.transitions) || 0,
      avg_hours: row.avg_hours != null ? Number(row.avg_hours) : null,
      median_hours: row.median_hours != null ? Number(row.median_hours) : null,
      min_hours: row.min_hours != null ? Number(row.min_hours) : null,
      max_hours: row.max_hours != null ? Number(row.max_hours) : null,
    });
    if (i % 2 === 1) {
      added.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.zebra } };
      });
    }
  });
  rSheet.views = [{ state: "frozen", ySplit: 1 }];
  rSheet.autoFilter = "A1:J1";

  /* ----------------------------- Descarga ------------------------------ */
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `reporte_estadisticas_compras.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
}
